// ======================================
// Blog Page Module
// ======================================

import { icons } from '../components.js';
import { articlesAPI } from '../lib/api.js';

// Helper function
function formatDate(dateStr) {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-IN', { month: 'short', day: 'numeric', year: 'numeric' });
}

// Blog List Page
export function renderBlogPage() {
  return `
    <section class="page-header">
      <div class="container">
        <div class="page-header-content">
          <h1 class="page-title">Interior Design Blog</h1>
          <p class="page-subtitle">
            Tips, trends, and inspiration for your home. Expert advice from our design team.
          </p>
        </div>
      </div>
    </section>

    <!-- Content Container -->
    <div id="blog-container">
        <!-- Loading Skeleton -->
        <section class="section" style="background-color: var(--color-surface);">
            <div class="container">
                <div style="text-align: center; padding: 4rem;">
                    <div class="spinner"></div>
                    <p style="margin-top: 1rem; color: var(--color-text-muted);">Loading articles...</p>
                </div>
            </div>
        </section>
    </div>

    <!-- Newsletter Section -->
    <section class="section" style="background-color: var(--color-surface);">
      <div class="container">
        <div class="newsletter-banner">
          <div class="newsletter-content">
            <h2 class="newsletter-title">Get Design Tips in Your Inbox</h2>
            <p class="newsletter-desc">
              Monthly inspiration, trends, and exclusive offers. No spam, ever.
            </p>
          </div>
          <form class="newsletter-form" id="newsletter-form">
            <input type="email" placeholder="Your email address" class="form-input" required>
            <button type="submit" class="btn btn-primary">Subscribe</button>
          </form>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="section cta-section">
      <div class="container">
        <div class="cta-content">
          <h2 class="cta-title">Ready to Start Your Project?</h2>
          <p class="cta-subtitle">
            Turn inspiration into reality with a free consultation.
          </p>
          <button class="btn btn-lg cta-btn" data-open-modal="lead-modal">
            Book Free Consultation
          </button>
        </div>
      </div>
    </section>
  `;
}

// Blog Post Detail Page
export function renderBlogPostPage(params) {
  return `
    <div id="blog-post-container" data-slug="${params.slug}">
       <!-- Loading Skeleton -->
       <section class="section">
           <div class="container" style="text-align: center; padding: 4rem;">
               <div class="spinner"></div>
               <p style="margin-top: 1rem; color: var(--color-text-muted);">Loading article...</p>
           </div>
       </section>
    </div>
  `;
}

// Initialize newsletter form & fetch data
export async function initNewsletter() {
  // 1. Newsletter Form Handler
  const form = document.getElementById('newsletter-form');
  if (form) {
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      const email = form.querySelector('input[type="email"]').value;
      console.log('Newsletter signup:', email);

      const button = form.querySelector('button');
      button.textContent = 'Subscribed!';
      button.disabled = true;
      form.querySelector('input').value = '';

      setTimeout(() => {
        button.textContent = 'Subscribe';
        button.disabled = false;
      }, 3000);
    });
  }

  // 2. Fetch Blog List Data (if on list page)
  const listContainer = document.getElementById('blog-container');
  if (listContainer) {
    try {
      const response = await articlesAPI.list();
      const posts = response.data || [];

      if (posts.length === 0) {
        listContainer.innerHTML = `
                    <section class="section">
                        <div class="container" style="text-align:center; padding: 4rem;">
                            <h3>No articles found.</h3>
                            <p>Check back later for updates!</p>
                        </div>
                    </section>
                `;
        return;
      }

      const featuredPosts = posts.filter(p => p.is_featured);
      const recentPosts = posts.filter(p => !p.is_featured);

      // Render Featured
      let html = '';
      if (featuredPosts.length > 0) {
        html += `
                <section class="section" style="background-color: var(--color-surface);">
                  <div class="container">
                    <h2 class="section-title" style="margin-bottom: var(--space-8);">Featured Articles</h2>
                    <div class="featured-posts-grid">
                      ${featuredPosts.map(post => `
                        <a href="/blog/${post.slug}" class="featured-post-card">
                          <div class="featured-post-img-wrapper">
                            <img src="${post.image || 'https://via.placeholder.com/800x400'}" alt="${post.title}" class="featured-post-img">
                            <span class="post-category">${post.category}</span>
                          </div>
                          <div class="featured-post-body">
                            <h3 class="featured-post-title">${post.title}</h3>
                            <p class="featured-post-excerpt">${post.excerpt}</p>
                            <div class="post-meta">
                              <span>${post.author}</span>
                              <span>•</span>
                              <span>${post.read_time || '5 min'} read</span>
                            </div>
                          </div>
                        </a>
                      `).join('')}
                    </div>
                  </div>
                </section>
                `;
      }

      // Render Recent
      html += `
            <section class="section" style="background-color: var(--color-base);">
              <div class="container">
                <h2 class="section-title" style="margin-bottom: var(--space-8);">Recent Articles</h2>
                <div class="blog-posts-grid">
                  ${recentPosts.map(post => `
                    <a href="/blog/${post.slug}" class="blog-post-card">
                      <img src="${post.image || 'https://via.placeholder.com/400x300'}" alt="${post.title}" class="blog-post-img">
                      <div class="blog-post-body">
                        <span class="post-category-small">${post.category}</span>
                        <h3 class="blog-post-title">${post.title}</h3>
                        <p class="blog-post-excerpt">${post.excerpt}</p>
                        <div class="post-meta">
                          <span>${formatDate(post.created_at)}</span>
                          <span>•</span>
                          <span>${post.read_time || '5 min'} read</span>
                        </div>
                      </div>
                    </a>
                  `).join('')}
                </div>
              </div>
            </section>
            `;

      listContainer.innerHTML = html;

    } catch (error) {
      console.error('Failed to load blog posts:', error);
      listContainer.innerHTML = `
                <section class="section">
                    <div class="container" style="text-align:center; padding: 4rem; color: var(--color-error);">
                        <p>Failed to load articles. Please try again later.</p>
                    </div>
                </section>
            `;
    }
  }

  // 3. Fetch Blog Post Detail (if on detail page)
  const detailContainer = document.getElementById('blog-post-container');
  if (detailContainer) {
    const slug = detailContainer.dataset.slug;
    try {
      const response = await articlesAPI.get(slug);
      const post = response.data;

      if (!post) throw new Error('Post not found');

      // Render Post
      detailContainer.innerHTML = `
                <article class="blog-article">
                  <header class="article-header">
                    <div class="container">
                      <a href="/blog" class="back-link">
                        ${icons.arrowLeft} Back to Blog
                      </a>
                      <span class="post-category">${post.category}</span>
                      <h1 class="article-title">${post.title}</h1>
                      <div class="article-meta">
                        <span>By ${post.author}</span>
                        <span>•</span>
                        <span>${formatDate(post.created_at)}</span>
                        <span>•</span>
                        <span>${post.read_time || '5 min'} read</span>
                      </div>
                    </div>
                  </header>

                  <div class="article-hero">
                    <img src="${post.image || 'https://via.placeholder.com/1200x600'}" alt="${post.title}" class="article-hero-img">
                  </div>

                  <div class="container">
                    <div class="article-content">
                      <p class="article-intro">${post.excerpt}</p>
                      ${post.content} <!-- HTML Content from DB -->
                      
                      <div style="text-align: center; margin-top: var(--space-8);">
                        <button class="btn btn-primary btn-lg" data-open-modal="lead-modal">
                          Book Free Consultation
                        </button>
                      </div>
                    </div>
                  </div>
                </article>
            `;

      // Re-bind modal triggers for the injected content
      if (window.initUI) window.initUI(); // Re-init specific parts if needed, but components.js handles document-level clicks maybe?
      // Actually components.js binds on load, so new elements need binding.
      // components.js: document.querySelectorAll('[data-open-modal]').forEach...
      // I should manually bind here or components.js logic needs to be delegated.
      // For now, simple re-bind:
      detailContainer.querySelectorAll('[data-open-modal]').forEach(btn => {
        btn.addEventListener('click', () => {
          const modalId = btn.getAttribute('data-open-modal');
          const modal = document.getElementById(modalId);
          if (modal) {
            modal.classList.add('active');
            document.body.classList.add('no-scroll');
          }
        });
      });

    } catch (error) {
      console.error('Failed to load blog post:', error);
      detailContainer.innerHTML = `
                <section class="page-header">
                    <div class="container">
                        <div class="page-header-content">
                            <h1 class="page-title">Article Not Found</h1>
                            <a href="/blog" class="btn btn-primary mt-6">View All Articles</a>
                        </div>
                    </div>
                </section>
            `;
    }
  }
}
